<!--@format-->
<template>
    <div class="psurvey-details-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Details</h3>
                <div class="svg-container widget-close-button" @click="closeWidget()">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-svg v-if="isLoading" />
                <div v-else>
                    <h1 class="survey-partner-title">Partner Info</h1>

                    <h2 class="survey-partner-subtitle">Partner</h2>
                    <div class="inline-content divider">
                        <img class="profile-picture" :src="partnerInformation.partnerProfile" />
                        <p class="survey-partner-text">
                            {{ partnerInformation.partnerName }}
                        </p>
                    </div>
                    <template
                        v-if="
                            partnerInformation.assignedUsers &&
                            partnerInformation.assignedUsers.length > 0
                        "
                    >
                        <h2 class="survey-partner-subtitle">Assigned Users</h2>
                        <div class="inline-content divider">
                            <psurvey-overview-assigned-users
                                :items="partnerInformation.assignedUsers"
                            />
                        </div>
                    </template>
                    <h2 class="survey-partner-subtitle">Survey Submission Date</h2>
                    <div class="survey-partner-text divider">
                        {{ partnerInformation.sendDate | momentDateMMMDYYYY }}
                        <span class="divider-date">|</span>
                        {{ partnerInformation.sendDate | momentTime }}
                    </div>
                    <div
                        id="survey-viewer-canvas-container"
                        class="survey-viewer-canvas-container"
                        @click.self="unselectAll"
                    >
                        <psurvey-viewer-canvas :config="config" />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="back-button" @click="closeWidget()"> Go Back </app-button>
                </div>
                <div class="action-container">
                    <app-button class="save-button" @click="handleSave()"> Save </app-button>
                    <div class="basic-info-pack border export-pdf">
                        <app-glossy-button
                            :loading="exportLoading"
                            class="export-button"
                            @click="handleExport"
                        >
                            {{ exportLoading ? 'Exporting...' : 'Export PDF' }}
                            <template v-if="!exportLoading">
                                <s7-export-svg />
                            </template>
                        </app-glossy-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AppButton = () => import('@/components/common/app-button');
const PsurveyOverviewAssignedUsers = () =>
    import('@/components/partner-survey/psurvey-overview-assigned-users');
const LoadingSvg = () => import('@/components/svg/loading-screen-svg');
const PSurveyViewerCanvas = () =>
    import('@/components/partner-survey/partner-survey-viewer/psurvey-viewer-canvas.vue');
const S7ExportSvg = () => import('@/components/svg/grid/s7-export-svg');
const AppGlossyButton = () => import('@/components/common/app-glossy-button');

import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus.js';
import { PartnerSurveySubmissionService } from '@/scripts/services/partner-survey/partner-survey-submission-service';
import { PartnerSurveyService } from '@/scripts/services/partner-survey/partner-survey-service';
import { PartnerSurveySubmissionMixin } from '@/mixins/partner-survey-submission-mixin.js';
import PsurveyIndividualExportMixin from '@/mixins/psurvey-individual-export-mixin';
import { isArray } from 'lodash';

export default {
    name: 'psurvey-details-widget',

    components: {
        'widget-close-svg': WidgetCloseSvg,
        'app-button': AppButton,
        'psurvey-overview-assigned-users': PsurveyOverviewAssignedUsers,
        'loading-svg': LoadingSvg,
        'psurvey-viewer-canvas': PSurveyViewerCanvas,
        's7-export-svg': S7ExportSvg,
        'app-glossy-button': AppGlossyButton,
    },

    mixins: [PartnerSurveySubmissionMixin, PsurveyIndividualExportMixin],

    provide() {
        return {
            $isAnswerMode: () => true,
        };
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            exportLoading: false,
            config: {},
            boundInputs: [],
            answers: [],
            agencyAccountId: '',
            isCompleted: false,
            partnerAccountId: this.widget.item.data.partnerName.partnerAccountId,
            partnerInformation: {
                surveyName: '',
                sendDate: null,
                partnerProfile: this.widget.item.data.partnerName.logoUrl,
                partnerName: this.widget.item.data.partnerName.name,
                assignedUsers: [],
            },
        };
    },

    computed: {
        partnerSurveyInstanceId() {
            return this.widget.item.data.id;
        },

        surveyId() {
            return this.widget.item.data.surveyId;
        },
    },

    created() {
        EventBus.$on('partner-survey:input-added', this.addElement);
        this.getData();

        // Load currencies
        this.$store.dispatch('loadCurrencies');
    },

    beforeDestroy() {
        EventBus.$off('partner-survey:input-added', this.addElement);
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleSave() {
            if (this.isCompleted) {
                return this.confirmUpdate();
            }
            this.save();
        },

        save() {
            this.isLoading = true;
            const payload = {
                completed: false,
                agencyAccountId: this.agencyAccountId,
                partnerAccountId: this.partnerAccountId,
                answers: this.boundInputs
                    .filter((item) =>
                        isArray(item.content.value)
                            ? item.content.value.length
                            : item.content.value,
                    )
                    .map(this.mapResponseItem),
            };
            PartnerSurveySubmissionService.post(
                this.partnerSurveyInstanceId,
                this.partnerAccountId,
                payload,
            )
                .then(() => {
                    EventBus.$emit('partner-survey-overview:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'Failed to update the partner survey response.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        confirmUpdate() {
            const actions = {
                confirm: {
                    label: 'Make Edits',
                    action: this.save,
                    type: 'removeNotIcon',
                },

                cancel: {
                    label: 'Cancel',
                },
            };

            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Do you want to edit this response?',
                    content: "<p>Editing a partner's response will change it to <b>draft.</b></p>",
                    actions,
                    confirmText: 'Make Edits',
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        getAnswers() {
            return new Promise((resolve, reject) => {
                PartnerSurveySubmissionService.get(
                    this.partnerSurveyInstanceId,
                    this.partnerAccountId,
                )
                    .then(resolve)
                    .catch(reject);
            });
        },

        getSurveyTemplate() {
            return new Promise((resolve, reject) => {
                PartnerSurveyService.getById({
                    id: this.surveyId,
                    accountId: this.agencyAccountId,
                    context: 'vendor',
                })
                    .then(resolve)
                    .catch(reject);
            });
        },

        getData() {
            this.isLoading = true;
            this.getAnswers()
                .then(({ data: { data } }) => {
                    const { AgencyAccountId, Status, AssignedUsers } = data.instance;
                    this.answers = data.answers;
                    this.agencyAccountId = AgencyAccountId;
                    this.isCompleted = Status === 'completed';
                    this.partnerInformation.assignedUsers =
                        AssignedUsers && AssignedUsers.map(this.getAssignedUsers);
                    return this.getSurveyTemplate();
                })
                .then(({ data: { data } }) => {
                    const [surveyData] = data;
                    this.config = JSON.parse(surveyData.SurveyTemplate);
                    this.partnerInformation.surveyName = surveyData.SurveyTitle;
                    this.partnerInformation.sendDate = this.$moment(surveyData.PublishedAt);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your partner survey.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        getAssignedUsers(item) {
            return {
                id: item.Id,
                name: `${item.FirstName} ${item.LastName}`,
                profilePicture: item.ProfileImageUrl || require('@/assets/images/avatar.png'),
            };
        },

        handleExport() {
            const exportData = {
                exportSource: 'details-widget',
                surveyId: this.surveyId,
                partnerAccountId: this.partnerAccountId,
                loader: 'exportLoading',
            };

            this.handleExportPartner(exportData);
        },

        addElement(item) {
            item.content.new = false;
            const answer = this.answers.find((answerItem) => {
                return item.id === answerItem.Field.FrontEndId;
            });
            if (answer) {
                this.getElementValueFormat(item, answer);
            }
            this.boundInputs.push(item);
        },
    },
};
</script>

<style>
.psurvey-details-widget {
    color: #382153;
}

.psurvey-details-widget .loading-screen {
    width: 100%;
    text-align: center;
    margin-left: 0px;
}

.psurvey-details-widget .transparent-btn {
    background: transparent;
    color: #877d88;
}

.psurvey-details-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.psurvey-details-widget .survey-partner-title {
    font: normal normal bold 25px/25px Nunito;
}

.psurvey-details-widget .survey-partner-subtitle {
    font: normal normal bold 16px/24px Roboto;
}

.psurvey-details-widget .survey-partner-text {
    font: normal normal normal 14px/21px Roboto;
    margin-bottom: 0px;
}

.psurvey-details-widget .divider-date {
    padding: 0 6px;
}

.psurvey-details-widget .profile-picture {
    background: #ffffff;
    border: 2px solid #ffffff;
    border-radius: 50%;
    height: 25px;
    overflow: hidden;
    width: 25px;
    background-size: cover;
    background-position: center;
}

.psurvey-details-widget .inline-content {
    display: flex;
    gap: 5px;
    align-items: center;
    margin-bottom: 16px;
}

.psurvey-details-widget .divider {
    border-bottom: 1px solid #e9e9ef;
    padding-bottom: 16px;
}

.psurvey-details-widget .survey-viewer-canvas-container {
    margin-top: 15px;
}

.psurvey-details-widget .action-container {
    display: flex;
    gap: 15px;
}

.psurvey-details-widget .action-container .save-button {
    border: 1px solid #18aeff;
    background-color: transparent;
    color: #18aeff;
    transition: none;
}

.psurvey-details-widget .action-container .save-button:hover {
    border: 2px solid #18aeff;
    background-color: #18aeff;
    color: #ffffff;
    padding: 0px 24px;
}

@media only screen and (max-width: 720px) {
    .psurvey-details-widget.widget .widget-footer {
        padding: 0px 90px 0px 35px;
    }
}

.psurvey-details-widget .app-button.export-button {
    color: #ffffff;
    background-color: #18aeff;
    border: 1px solid #18aeff;
}

.psurvey-details-widget .app-button:hover.export-button {
    filter: brightness(90%);
}

.psurvey-details-widget .app-glossy-button button.btn .s7-export-svg {
    padding-left: 5px;
}

.psurvey-details-widget .app-glossy-button button.btn .s7-export-svg,
.psurvey-details-widget .app-glossy-button button.btn .s7-export-svg .a {
    stroke: #fff;
    fill: transparent;
}

.psurvey-details-widget .app-button .s7-export-svg,
.psurvey-details-widget .app-button .s7-export-svg .a {
    fill: transparent;
    stroke: #ffffff;
    width: 15px;
}

.psurvey-details-widget .status-chip {
    border-radius: 22px;
    display: flex;
    justify-content: center;
    font-weight: 500;
    padding: 8px 40px;
    width: 70%;
    text-align: center;
}

.psurvey-details-widget .export-pdf {
    display: grid;
    justify-items: center;
    align-content: center;
}
</style>
